import { useCallback, useState } from 'react';
import { Box, CardMedia, Grid, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { DateTime } from 'luxon';

import { DiligenceStore } from 'stores/diligence/DiligenceStore';
import { salesforceTimeZone } from 'utils/config';
import { InspectionImageModal } from './modals/InspectionImageModal';
import AmplitudeService from 'services/amplitude.service';
import { useRootStore } from 'stores/RootStore';

function renderDate(date: string): string {
  return DateTime.fromISO(date).setZone(salesforceTimeZone).toFormat('MM/dd/yy');
}

type InspectionProps = {
  diligenceStore: DiligenceStore;
};

const Inspection = observer(({ diligenceStore }: InspectionProps) => {
  const { job } = diligenceStore;
  const { authStore } = useRootStore();

  const [inspectionPhotoModalState, setInspectionPhotoModalState] = useState<{ open: boolean; url: string | null }>({
    open: false,
    url: null,
  });

  const inspectionDate = job?.inspection?.inspectionDate;
  const inspectionNote = job?.inspection?.note;

  const onImageClick = useCallback(
    (category: string, cdnUrl: string) => {
      if (
        !authStore?.currentUser?.roles?.some((role) => ['internal', 'admin'].includes(role)) ||
        !/@boweryvaluation\.com(\.invalid)?$/i.test(authStore?.currentUser?.email)
      ) {
        AmplitudeService.trackEvent('Inspection Photo', { address: job.address, name: job.name, cdnUrl, category });
      }
      setInspectionPhotoModalState({ open: true, url: cdnUrl });
    },
    [authStore?.currentUser, job.address, job.name]
  );

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, padding: '16px' }}>
        <Grid container>
          <Grid item xs={12}>
            <Typography variant="h4" sx={{ marginBottom: '24px' }}>
              <strong>Inspection</strong>
            </Typography>
          </Grid>
        </Grid>

        <Box>
          <Typography sx={{ mb: '8px' }} variant="body1" data-testid="inspection-date">
            <strong>Scheduled Date: </strong>
            {inspectionDate ? renderDate(inspectionDate) : 'Not scheduled yet'}
          </Typography>

          {inspectionNote && (
            <Typography sx={{ mb: '8px' }} variant="body1" data-testid="inspection-date">
              <strong>Inspection Note: </strong>
              {inspectionNote}
            </Typography>
          )}

          {job?.inspection?.categories && (
            <div>
              <Typography variant="h6" sx={{ marginTop: '24px', marginBottom: '16px' }}>
                <strong>Property Photos</strong>
              </Typography>
              {job?.inspection?.categories.map(({ name, photos }) => (
                <div key={name}>
                  <Typography variant="body1">
                    <strong>{name}</strong>
                  </Typography>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4%', marginTop: '10px' }}>
                    {photos.map(({ cdnUrl }) => (
                      <div
                        key={cdnUrl}
                        onClick={() => onImageClick(name, cdnUrl)}
                        style={{
                          maxHeight: '290px',
                          maxWidth: '290px',
                          flex: '0 48%',
                          marginBottom: '4%',
                          cursor: 'pointer',
                        }}
                      >
                        <CardMedia sx={{ paddingTop: '100%', height: 0, borderRadius: 4 }} image={cdnUrl} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Box>
      </div>
      {inspectionPhotoModalState.open && inspectionPhotoModalState.url && (
        <InspectionImageModal
          url={inspectionPhotoModalState.url}
          onCancel={() =>
            setInspectionPhotoModalState({
              open: false,
              url: null,
            })
          }
        />
      )}
    </>
  );
});

export { Inspection };
